import type { VariantProps } from 'class-variance-authority';
import { cn, Icon } from '@lqa-frontend/ui-system';
import type { IconName } from '@lqa-frontend/ui-system';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { Button } from '../buttons/Button';
import { useState } from 'react';

const AlertMessageContext = React.createContext<{
	onClose?: () => void;
} | null>(null);

const alertVariants = cva(
	'relative w-full rounded-s border text-text-primary [&>svg]:size-16 [&>svg]:text-text-primary [&>svg]:flex-shrink-0',
	{
		variants: {
			variant: {
				default: 'border-outline-interactive bg-interactive-light',
				success: 'border-outline-success bg-success-light',
				warning: 'border-outline-warning bg-warning-light ',
				danger: 'border-outline-danger bg-danger-light ',
			},
			size: {
				default: 'px-12 py-8 text-14 [&>svg]:translate-y-0.5',
				s: 'px-8 py-10 text-12 font-semibold',
				m: 'px-12 py-8 text-16 [&>svg]:translate-y-0.5',
				l: 'px-14 py-10 text-16 [&>svg]:translate-y-0.5',
			},
			layout: {
				vertical: 'flex gap-6',
				horizontal: 'flex gap-6',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
			layout: 'vertical',
		},
	}
);

interface IAlertMessageIconMap {
	default: IconName;
	success: IconName;
	warning: IconName;
	danger: IconName;
}

const iconMap: IAlertMessageIconMap = {
	default: 'infoCircle',
	success: 'checkCircle',
	warning: 'exclamationTriangle',
	danger: 'infoCircle',
};

interface AlertButton {
	label: string;
	onClick: () => void;
}

interface AlertMessageProps
	extends React.ComponentProps<'div'>, VariantProps<typeof alertVariants> {
	shouldShowIcon?: boolean;
	link?: AlertButton;
	cta?: AlertButton;
	layout?: 'vertical' | 'horizontal';
	onClose?: () => void;
}

function AlertMessage({
	className,
	variant = 'default',
	layout = 'vertical',
	size = 'default',
	shouldShowIcon = false,
	link,
	cta,
	onClose,
	children,
	...props
}: AlertMessageProps) {
	const [isVisible, setIsVisible] = useState(true);
	const iconName = iconMap[variant || 'default'];

	const handleClose = () => {
		if (onClose) {
			onClose();
		} else {
			setIsVisible(false);
		}
	};
	if (!isVisible && !onClose) {
		return null;
	}

	return (
		<AlertMessageContext.Provider value={{ onClose: handleClose }}>
			<div
				data-slot="alert"
				role="alert"
				className={cn(alertVariants({ variant, size, layout }), className)}
				{...props}
			>
				{shouldShowIcon && <Icon name={iconName} />}
				<div className="flex flex-col gap-4 min-w-0 flex-1">
					{children}
					{(link || cta) && layout === 'vertical' && (
						<AlertMessageActions link={link} cta={cta} layout={layout} />
					)}
				</div>
				{(link || cta) && layout === 'horizontal' && (
					<AlertMessageActions link={link} cta={cta} layout={layout} />
				)}
			</div>
		</AlertMessageContext.Provider>
	);
}

function AlertMessageTitle({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="alert-title"
			className={cn(
				'line-clamp-1 min-h-16 font-regular tracking-tight',
				className
			)}
			{...props}
		/>
	);
}

function AlertMessageDescription({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="alert-description"
			className={cn(
				'text-neutral flex flex-col items-start gap-4 [&_p]:leading-relaxed',
				className
			)}
			{...props}
		/>
	);
}

interface AlertMessageActionsProps {
	link?: AlertButton;
	cta?: AlertButton;
	layout?: 'vertical' | 'horizontal';
}

function AlertMessageActions({
	link,
	cta,
	layout = 'vertical',
}: AlertMessageActionsProps) {
	const linkButton = link && (
		<Button
			variant="link"
			size="s"
			className="text-text-primary  px-0 underline"
			onClick={link.onClick}
		>
			{link.label}
		</Button>
	);

	const ctaButton = cta && (
		<Button
			variant="default"
			size="s"
			className="text-text-primary py-4 border-text-primary bg-transparent"
			onClick={cta.onClick}
		>
			{cta.label}
		</Button>
	);

	return (
		<div
			data-slot="alert-actions"
			className={cn(
				'flex items-center gap-12 flex-shrink-0',
				layout === 'horizontal' ? 'self-end' : 'mt-6'
			)}
		>
			{layout === 'vertical' ? (
				<>
					{ctaButton}
					{linkButton}
				</>
			) : (
				<>
					{linkButton}
					{ctaButton}
				</>
			)}
		</div>
	);
}

function AlertMessageClose({
	className,
	onClick,
	...props
}: React.ComponentProps<'button'>) {
	const context = React.useContext(AlertMessageContext);

	const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		context?.onClose?.();
		onClick?.(e);
	};

	return (
		<button
			data-slot="alert-close"
			className={cn(
				'absolute top-8 right-8 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-16',
				className
			)}
			aria-label="Close"
			onClick={handleClick}
			{...props}
		>
			<Icon name="xLg" />
			<span className="sr-only">Close</span>
		</button>
	);
}

export {
	AlertMessage,
	AlertMessageTitle,
	AlertMessageDescription,
	AlertMessageClose,
};
